/**
 * PrescriptionReadOnlyView — Read-only display of a saved digital prescription.
 *
 * Shown when a visit is COMPLETED. Renders all prescription data in a clean,
 * non-editable card with:
 *   - Chief Complaint (C/O)
 *   - Attached images
 *   - Diagnosis
 *   - Medicine table
 *   - Investigations
 *   - Doctor Notes
 *   - Follow-up info
 *   - Version badge (if edited post-completion)
 *   - "View prescription" button
 *   - "Edit Prescription" toggle (DOCTOR/ADMIN only)
 *
 * The action VERB is "View", not "Generate" (owner, this task): this card is a
 * record surface on both its callers, and the same rows already say "View" for
 * a scanned/file-backed prescription. Prescriptions are not stored as PDFs and
 * will not be — the document is rebuilt on demand — but the reader is not the
 * one authoring it, so they are not told they are. The genuine creation action
 * (`DigitalPrescriptionForm`'s "Generate Prescription (…)") keeps its verb.
 *
 * `canGeneratePdf` (bk-861, D24, mirrors the `reportOnly`/F7 shape already
 * used for lab actions): viewing a saved prescription is not authoring one,
 * so the SCREEN decides whether the download action is offered, not the
 * `GENERATE_PRESCRIPTION_PDF` permission map alone. Prop omitted (every
 * caller before this task, e.g. `ManageVisitPage` via `VisitActionsPanel`)
 * → falls back to `usePermission('GENERATE_PRESCRIPTION_PDF')`, byte-identical
 * to prior behaviour. The read-only patient card passes `canGeneratePdf`
 * explicitly — the PDF endpoint (`GET /prescription/visit/{id}/pdf`) is
 * already gated by `require_org_user` only, so this grants no access the
 * backend didn't already allow. Editing (`onEdit`/`ADD_PRESCRIPTION`) is
 * untouched by this prop and stays permission-gated everywhere.
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePermission } from '../../context/PermissionContext';
import { getFileDownloadUrl } from '../../services/api/visitMetadataApi';
import { SURGERY_SECTION_LABEL } from '../../utils/rxMemory';
// Dose rendering is NOT local to this card. The hand-built
// `${morning}-${afternoon}-${night}` template that used to sit further down
// cannot spell a half dose: master's RX half-dose work made `0.5` a real
// stored quantity that must READ as "½", and only `formatDoseSchedule` knows
// that spelling (its golden round-trip fixture is mirrored in
// `app/core/dose.py`). Same `m-a-n` output shape, same '' for no schedule.
import { formatDoseSchedule as slotDisplay } from '../../utils/rxDosage';

/**
 * Type scale (owner: "not viewable properly … the font is too tiny").
 *
 * The card used to live entirely between 8px and 12px — clinical body text
 * (drug names, dosage, notes) at 11px and metadata at 8px. Every step is
 * lifted ONE for ONE, so the hierarchy is unchanged and nothing is flattened:
 *
 *   8px → 11px   9px → 12px   10px → 13px   11px → 14px   12px → 15px
 *
 * The two rules this scale exists to hold: body text is at least 14px, and
 * NOTHING anywhere on this card is below 11px. Named steps, not literals, so
 * a future edit can't quietly reintroduce an 8px line
 * (`PrescriptionReadOnlyView.typeScale.test.jsx` walks the DOM and fails on
 * any inline font-size under the floor).
 *
 * The Georgia serif treatment is deliberate — a prescription should read like
 * paper — and is untouched.
 */
const FS = {
  meta: '0.6875rem',    // 11px — FLOOR: timestamp, author, version badge, medicine note
  detail: '0.75rem',    // 12px — timing, duration, chips, medicine index, edit reason
  support: '0.8125rem', // 13px — section headings, strength/form/dosage, error line
  body: '0.875rem',     // 14px — clinical text: complaint, diagnosis, drug name, notes
  action: '0.9375rem',  // 15px — buttons and the card's own icon
};

export default function PrescriptionReadOnlyView({
  prescriptionEntry,
  visitId,
  onEdit,
  onGeneratePdf,
  pdfGenerating = false,
  canGeneratePdf,
}) {
  const { isDark } = useTheme();
  const hasPdfPermission = usePermission('GENERATE_PRESCRIPTION_PDF');
  const canEdit = usePermission('ADD_PRESCRIPTION');
  // `canGeneratePdf` prop omitted (undefined) → prior permission-gated
  // behaviour, unchanged. Passed explicitly → screen's call, not the map's.
  const showPdfButton = canGeneratePdf === undefined ? hasPdfPermission : canGeneratePdf;
  const [pdfError, setPdfError] = useState(null);
  // meta_id -> presigned S3 URL, resolved lazily once attached_images is known
  const [imageUrls, setImageUrls] = useState({});

  const attached_images = prescriptionEntry?.attached_images || [];

  // Resolve a presigned URL per attached image. Re-runs when the set of
  // image meta_ids changes (new Rx loaded, or images added on edit+resave).
  const imageIds = attached_images.map((img) => img.meta_id).join(',');
  useEffect(() => {
    if (!visitId || attached_images.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        attached_images.map(async (img) => {
          try {
            const { url } = await getFileDownloadUrl(visitId, 'PRESCRIPTION_IMAGE', img.meta_id);
            return [img.meta_id, url];
          } catch {
            return [img.meta_id, null];
          }
        })
      );
      if (!cancelled) setImageUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, imageIds]);

  if (!prescriptionEntry) return null;

  // `advised_surgeries` is `string[] | null` on the wire (G2) — a legacy row
  // with no field at all and an explicit `[]` are treated identically.
  const advisedSurgeries = prescriptionEntry.advised_surgeries || [];

  const {
    chief_complaint,
    allergies,
    diagnosis,
    doctor_notes,
    created_at,
    added_by_name,
    version,
    edit_reason,
  } = prescriptionEntry;

  // Array fields are COERCED, never destructured with a default (bk-1182).
  // `medicines` / `investigations` are `Optional[List[...]] = None` on the
  // backend and reach us as JSON `null` on legacy rows; a destructuring
  // default fires only for `undefined`, so `= []` would leave `null` in place
  // and `.length` / `.map()` below would throw — white-screening this card on
  // both the patient card and the COMPLETED-visit panel. These are the only
  // two array fields this component reads.
  const medicines = prescriptionEntry.medicines || [];
  const investigations = prescriptionEntry.investigations || [];

  // Style tokens
  const pad = isDark ? 'rgba(255,255,255,0.025)' : '#fefdfb';
  const padBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(180,160,120,0.2)';
  const ink = isDark ? '#e8e9ed' : '#1a1a2e';
  const inkLight = isDark ? '#9ca3af' : '#6b7280';
  const teal = '#0d9488';
  const tealBg = isDark ? 'rgba(13,148,136,0.08)' : 'rgba(13,148,136,0.04)';
  const sectionHd = { fontSize: FS.support, fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem', fontFamily: 'system-ui, sans-serif' };
  const sectionBody = { fontSize: FS.body, color: ink, lineHeight: 1.5, fontFamily: "'Georgia', serif" };

  const handlePdf = async () => {
    setPdfError(null);
    try {
      await onGeneratePdf?.();
    } catch (err) {
      setPdfError(err?.message || 'Failed to generate PDF');
    }
  };

  return (
    <div style={{
      background: pad,
      borderRadius: '0.625rem',
      border: `1px solid ${padBorder}`,
      fontFamily: "'Georgia', 'Times New Roman', serif",
      overflow: 'hidden',
    }}>
      {/* Header bar */}
      <div style={{
        padding: '0.5rem 0.75rem',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(180,160,120,0.15)'}`,
        // Wraps, so the bigger type can't push the by-line out of a narrow
        // column (`VisitActionsPanel`) — the card root is `overflow: hidden`,
        // which would CLIP an overflowing header rather than scroll it.
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.25rem',
        background: isDark ? 'rgba(13,148,136,0.04)' : 'rgba(13,148,136,0.02)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontSize: FS.action }}>📋</span>
          <span style={{ fontSize: FS.body, fontWeight: 700, color: ink, fontFamily: 'system-ui, sans-serif' }}>
            Prescription
          </span>
          {version && version > 1 && (
            <span style={{
              fontSize: FS.meta, padding: '0.1rem 0.35rem', borderRadius: '0.25rem',
              background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)',
              color: isDark ? '#fbbf24' : '#d97706', fontWeight: 700, fontFamily: 'system-ui, sans-serif',
            }}>V{version} — Edited</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', minWidth: 0 }}>
          {created_at && (
            <span style={{ fontSize: FS.meta, color: inkLight, fontFamily: 'system-ui, sans-serif' }}>
              {new Date(created_at).toLocaleString()}
            </span>
          )}
          {added_by_name && (
            <span style={{ fontSize: FS.meta, color: inkLight, fontFamily: 'system-ui, sans-serif' }}>
              by {added_by_name}
            </span>
          )}
        </div>
      </div>

      {/* Version edit reason (if post-completion edit) */}
      {edit_reason && (
        <div style={{
          padding: '0.25rem 0.75rem',
          background: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.03)',
          fontSize: FS.detail, color: isDark ? '#fbbf24' : '#d97706',
          fontFamily: 'system-ui, sans-serif',
        }}>
          ✏️ Edit reason: {edit_reason}
        </div>
      )}

      {/* Body */}
      <div style={{ padding: '0.5rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>

        {/* Allergies — absent/null/'' render nothing, same rule as
            advised_surgeries (contract C2). */}
        {allergies && (
          <div>
            <div style={sectionHd}>Allergies</div>
            <div style={sectionBody}>{allergies}</div>
          </div>
        )}

        {/* Chief Complaint */}
        {chief_complaint && (
          <div>
            <div style={sectionHd}>C/o (Chief Complaint)</div>
            <div style={sectionBody}>{chief_complaint}</div>
          </div>
        )}

        {/* Attached Images */}
        {attached_images.length > 0 && (
          <div>
            <div style={sectionHd}>📷 Attached Images ({attached_images.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
              {attached_images.map((img) => {
                const url = imageUrls[img.meta_id];
                return (
                  <a
                    key={img.meta_id}
                    href={url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'block', width: '3.5rem', height: '3.5rem',
                      borderRadius: '0.375rem', overflow: 'hidden',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      cursor: url ? 'pointer' : 'default',
                    }}
                  >
                    {url && (
                      <img
                        src={url}
                        alt={img.file_name || 'Attached image'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Diagnosis */}
        {diagnosis && (
          <div>
            <div style={sectionHd}>Diagnosis</div>
            <div style={sectionBody}>{diagnosis}</div>
          </div>
        )}

        {/* Medicines */}
        {medicines.length > 0 && (
          <div>
            <div style={sectionHd}>℞ Medicines ({medicines.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {medicines.map((med, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap',
                  padding: '0.25rem 0.375rem',
                  borderRadius: '0.375rem',
                  background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
                }}>
                  <span style={{ fontSize: FS.detail, fontWeight: 700, color: teal, minWidth: '1rem' }}>{i + 1}.</span>
                  {/* The drug name is the thing a reader came for — body size,
                      and allowed to break rather than widen the row. */}
                  <span style={{ fontSize: FS.body, fontWeight: 600, color: ink, wordBreak: 'break-word' }}>{med.medicine || med.medicine_input}</span>
                  {med.strength && <span style={{ fontSize: FS.support, color: isDark ? '#2dd4bf' : '#0f766e' }}>{med.strength}</span>}
                  {med.form && <span style={{ fontSize: FS.support, color: inkLight }}>({med.form})</span>}
                  {med.dosage_schedule && (
                    <span style={{ fontSize: FS.support, color: isDark ? '#60a5fa' : '#3b82f6', fontWeight: 600, fontFamily: 'system-ui, sans-serif' }}>
                      {slotDisplay(med.dosage_schedule)}
                    </span>
                  )}
                  {med.timing && <span style={{ fontSize: FS.detail, color: inkLight }}>{med.timing}</span>}
                  {med.duration && <span style={{ fontSize: FS.detail, color: inkLight }}>{med.duration}</span>}
                  {med.notes && <span style={{ fontSize: FS.meta, color: inkLight, fontStyle: 'italic' }}>— {med.notes}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Investigations */}
        {investigations.length > 0 && (
          <div>
            <div style={sectionHd}>🧪 Investigations</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {investigations.map((inv, i) => (
                <span key={i} style={{
                  fontSize: FS.detail, padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                  background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                  color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500, fontFamily: 'system-ui, sans-serif',
                }}>{inv}</span>
              ))}
            </div>
          </div>
        )}

        {/* Advised surgeries — G2 `advised_surgeries: string[] | null`; a
            legacy row and an explicit empty list both render nothing (no
            heading, no blank band). Read-only: text only, no `×`. */}
        {advisedSurgeries.length > 0 && (
          <div>
            <div style={sectionHd}>{`🔪 ${SURGERY_SECTION_LABEL}`}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {advisedSurgeries.map((s, i) => (
                <span key={i} style={{
                  fontSize: FS.detail, padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                  background: isDark ? 'rgba(124,58,237,0.2)' : '#ede9fe',
                  color: isDark ? '#c4b5fd' : '#5b21b6', fontWeight: 500, fontFamily: 'system-ui, sans-serif',
                  border: `1px solid ${isDark ? 'rgba(196,181,253,0.4)' : 'rgba(124,58,237,0.35)'}`,
                }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Doctor Notes */}
        {doctor_notes && (
          <div>
            <div style={sectionHd}>📝 Notes</div>
            <div style={{ ...sectionBody, fontStyle: 'italic' }}>{doctor_notes}</div>
          </div>
        )}

        {/* Follow-up is single-sourced on the visit, not the prescription document. */}
      </div>

      {/* Action buttons */}
      <div style={{
        padding: '0.5rem 0.75rem',
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(180,160,120,0.15)'}`,
        display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
      }}>
        {/* View prescription (rebuilds the document on demand) */}
        {showPdfButton && (
          <button
            onClick={handlePdf}
            disabled={pdfGenerating}
            style={{
              flex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(13,148,136,0.3)' : 'rgba(13,148,136,0.25)'}`,
              background: tealBg,
              color: isDark ? '#5eead4' : teal,
              fontSize: FS.action, fontWeight: 600,
              cursor: pdfGenerating ? 'wait' : 'pointer',
              opacity: pdfGenerating ? 0.6 : 1,
              transition: 'background 0.15s, opacity 0.15s',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            {pdfGenerating ? '⏳ Preparing…' : '📄 View prescription'}
          </button>
        )}

        {/* Edit Prescription */}
        {canEdit && onEdit && (
          <button
            onClick={onEdit}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
              background: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.03)',
              color: isDark ? '#fbbf24' : '#d97706',
              fontSize: FS.action, fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            ✏️ Edit Prescription
          </button>
        )}
      </div>

      {pdfError && (
        <div style={{
          padding: '0.25rem 0.75rem 0.5rem',
          fontSize: FS.support, color: isDark ? '#fca5a5' : '#dc2626',
          textAlign: 'center', fontFamily: 'system-ui, sans-serif',
        }}>
          {pdfError}
        </div>
      )}
    </div>
  );
}
